/**
 * VireoHR - Super Admin Branch Management
 * Only accessible by super admin
 * Features: Add/Edit/Delete branches, Set max employees, Set coordinates
 */

import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Input from '@/components/Input';
import Layout from '@/components/Layout';
import { firestore } from '@/lib/firebase/client';


interface Branch {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  geofenceRadius: number;
  maxEmployees: number;
  tenantId: string;
  tenantName?: string;
  active: boolean;
}

interface Tenant {
  id: string;
  name: string;
}

export default function BranchManagement() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    geofenceRadius: '100',
    maxEmployees: '10',
    tenantId: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch tenants
      const tenantsSnap = await getDocs(collection(firestore, 'tenants'));
      const tenantsData = tenantsSnap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      })) as Tenant[];
      setTenants(tenantsData);

      // Fetch branches
      const branchesSnap = await getDocs(collection(firestore, 'branches'));
      const branchesData = branchesSnap.docs.map(doc => {
        const data = doc.data();
        const tenant = tenantsData.find(t => t.id === data.tenantId);
        return {
          id: doc.id,
          ...data,
          tenantName: tenant?.name || 'Unknown'
        };
      }) as Branch[];
      
      setBranches(branchesData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.address || !formData.tenantId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const branchData = {
        name: formData.name,
        address: formData.address,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        geofenceRadius: parseInt(formData.geofenceRadius),
        maxEmployees: parseInt(formData.maxEmployees),
        tenantId: formData.tenantId,
        active: formData.active,
        updatedAt: serverTimestamp()
      };

      if (editingBranch) {
        // Update existing branch
        await updateDoc(doc(firestore, 'branches', editingBranch.id), branchData);
        toast.success('Branch updated successfully');
      } else {
        // Create new branch
        await addDoc(collection(firestore, 'branches'), {
          ...branchData,
          createdAt: serverTimestamp()
        });
        toast.success('Branch created successfully');
      }

      // Reset form
      setFormData({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        geofenceRadius: '100',
        maxEmployees: '10',
        tenantId: '',
        active: true
      });
      setShowAddForm(false);
      setEditingBranch(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save branch:', error);
      toast.error('Failed to save branch');
    }
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      address: branch.address,
      latitude: branch.latitude.toString(),
      longitude: branch.longitude.toString(),
      geofenceRadius: branch.geofenceRadius.toString(),
      maxEmployees: branch.maxEmployees?.toString() || '10',
      tenantId: branch.tenantId,
      active: branch.active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (branchId: string) => {
    if (!confirm('Are you sure you want to delete this branch?')) return;

    try {
      await deleteDoc(doc(firestore, 'branches', branchId));
      toast.success('Branch deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete branch:', error);
      toast.error('Failed to delete branch');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingBranch(null);
    setFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      geofenceRadius: '100',
      maxEmployees: '10',
      tenantId: '',
      active: true
    });
  };

  if (loading) {
    return (
      <Layout title="Branch Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Branch Management">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-gray-900">All Branches</h1>
          {!showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-[#0D9488] text-white hover:bg-[#0a7d73]"
            >
              Add Branch
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card>
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">
                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
              </h3>

              {/* Tenant Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant *
                </label>
                <select
                  value={formData.tenantId}
                  onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                  required
                >
                  <option value="">Select Tenant</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Branch Name */}
              <Input
                label="Branch Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Main Office"
                required
              />

              {/* Address */}
              <Input
                label="Address *"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City"
                required
              />

              {/* Coordinates */}
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Latitude *"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="31.9454"
                  required
                />
                <Input
                  label="Longitude *"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="35.9284"
                  required
                />
              </div>

              {/* Geofence & Max Employees */}
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Geofence Radius (m) *"
                  type="number"
                  value={formData.geofenceRadius}
                  onChange={(e) => setFormData({ ...formData, geofenceRadius: e.target.value })}
                  required
                />
                <Input
                  label="Max Employees *"
                  type="number"
                  value={formData.maxEmployees}
                  onChange={(e) => setFormData({ ...formData, maxEmployees: e.target.value })}
                  required
                />
              </div>

              {/* Active Status */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-[#0D9488] border-gray-300 rounded focus:ring-[#0D9488]"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>

              {/* Actions */}
              <div className="flex gap-2">
                <Button type="submit" className="bg-[#0D9488] text-white hover:bg-[#0a7d73]">
                  {editingBranch ? 'Update' : 'Create'} Branch
                </Button>
                <Button type="button" onClick={handleCancel} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Branches List */}
        {branches.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No branches found</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {branches.map((branch) => (
              <Card key={branch.id}>
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">{branch.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{branch.tenantName}</p>
                      <p className="text-xs text-gray-500">{branch.address}</p>
                    </div>
                    <Badge variant={branch.active ? 'success' : 'default'}>
                      {branch.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {/* Details */}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Coordinates</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">
                          {branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Geofence Radius</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{branch.geofenceRadius}m</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Max Employees</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{branch.maxEmployees || 'Not set'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <Button
                      onClick={() => handleEdit(branch)}
                      variant="outline"
                      className="text-sm"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(branch.id)}
                      variant="outline"
                      className="text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
